import React from 'react';
import Pill from './Pill';
import IconButton from './IconButton';
import { cs2StatusView, CS2_SORT_RANK } from '../utils/cs2StatusView';
import { relativeTime } from '../utils/relativeTime';

function StatusDot({ color }) {
  const tone = { success: 'text-steam-success', warn: 'text-steam-warn', danger: 'text-steam-danger', muted: 'text-surface-muted' }[color] || 'text-surface-muted';
  return <span className={`inline-block ml-2 ${tone}`}>●</span>;
}

function cs2Pill(cs2) {
  if (cs2.kind === 'not_tracked') return <span className="text-[11px] text-surface-muted">—</span>;
  const kind = { ready: 'ready', cooldown: 'cooldown', login_error: 'banned', quarantined: 'banned', pending: 'muted' }[cs2.kind] || 'info';
  return <Pill kind={kind} label={cs2.label.replace(/^.*? /, '')} tooltip={cs2.tooltip} />;
}

function rowStatusDot(cs2, isBanned) {
  if (isBanned) return 'danger';
  if (cs2.kind === 'quarantined' || cs2.kind === 'login_error') return 'danger';
  if (cs2.kind === 'cooldown') return 'warn';
  if (cs2.kind === 'ready') return 'success';
  return 'muted';
}

export default function AccountsTable({
  accounts, sortConfig, onSort, onDelete, onUnquarantine, onCopy, onOpenNotes,
  onCheckNow, checkingIds, role,
  filtersActive, onClearFilters
}) {
  const sortIndicator = (key) => (sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '');
  const canCopy = role === 'manager' || role === 'user';

  const header = (key, label, widthClass) => (
    <div
      className={`cursor-pointer select-none ${widthClass}`}
      onClick={() => onSort(key)}
    >
      {label}{sortIndicator(key)}
    </div>
  );

  if (accounts.length === 0) {
    return (
      <div className="bg-surface-panel border border-surface-border p-10 text-center text-surface-muted text-sm">
        <div>No accounts match these filters.</div>
        {filtersActive && onClearFilters && (
          <button
            type="button"
            onClick={onClearFilters}
            className="mt-4 px-[14px] py-2 bg-transparent text-accent border border-surface-border text-[11px] font-semibold uppercase tracking-wider rounded-[2px] hover:bg-surface-border/60"
          >Clear filters</button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-surface-panel border border-surface-border overflow-x-auto">
      <div className="grid grid-cols-[36px_minmax(0,1fr)_100px_70px_100px_80px_100px_100px_180px] gap-3 px-[14px] py-[10px] bg-surface-header border-b border-surface-border text-[10px] font-bold uppercase tracking-[0.08em] text-surface-muted items-center">
        <div />
        {header('displayName', 'Account')}
        {header('cs2Status', 'CS2')}
        {header('bans', 'Bans')}
        {header('cs2LastPlayed', 'Last played')}
        {header('cs2Hours', 'Hours')}
        {header('lastOnline', 'Last online')}
        {header('lastCheckedAt', 'Checked')}
        <div className="text-right">Actions</div>
      </div>

      {accounts.map((acc) => {
        const lbs = acc.lastBanState || {};
        const isVacBanned = (lbs.numberOfVACBans || 0) > 0;
        const isGameBanned = (lbs.numberOfGameBans || 0) > 0;
        const isCommunityBanned = !!lbs.communityBanned;
        const isEconomyBanned = lbs.economyBan && lbs.economyBan !== 'none';
        const isBanned = isVacBanned || isGameBanned || isCommunityBanned || isEconomyBanned;
        const cs2 = cs2StatusView(acc);
        const tintRow = isBanned || cs2.kind === 'quarantined';
        const dot = rowStatusDot(cs2, isBanned);
        const isCredentialed = acc.accountType === 'credentialed';
        const online = relativeTime(acc.lastOnline);
        const checked = relativeTime(acc.lastBanState?.lastCheckedAt);
        const cs2Played = relativeTime(acc.cs2State?.cs2LastPlayed);
        // Pin Hours formatting to en-US so the thousand separators are
        // consistent regardless of the operator's browser locale.
        const cs2PlaytimeMinutes = acc.cs2State?.cs2PlaytimeMinutes;
        const cs2HoursLabel = (cs2PlaytimeMinutes == null)
          ? '—'
          : `${Math.floor(cs2PlaytimeMinutes / 60).toLocaleString('en-US')}h`;
        const cs2HoursTooltip = (cs2PlaytimeMinutes == null)
          ? 'Unknown — never seen, private profile, or pre-feature data'
          : `${cs2PlaytimeMinutes.toLocaleString('en-US')} minutes`;

        return (
          <div
            key={acc._id}
            className={`grid grid-cols-[36px_minmax(0,1fr)_100px_70px_100px_80px_100px_100px_180px] gap-3 px-[14px] py-[9px] border-b border-surface-base items-center text-xs ${tintRow ? 'bg-[rgba(217,65,38,0.04)]' : ''}`}
          >
            <div>
              <img
                src={acc.avatarUrl || ''}
                alt=""
                className={`w-7 h-7 rounded-[2px] ${isBanned ? 'grayscale brightness-75' : ''}`}
              />
            </div>
            <div className="min-w-0">
              <div className="truncate">
                {isBanned
                  ? <span className="text-surface-muted font-semibold line-through">{acc.displayName || 'Unknown'}</span>
                  : <a
                      href={`https://steamcommunity.com/profiles/${acc.steamId64}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-accent font-semibold hover:underline"
                      title={acc.displayName}
                    >{acc.displayName || 'Unknown'}</a>}
                <StatusDot color={dot} />
                {!isCredentialed && (
                  <span className="text-[9px] text-surface-muted ml-2 uppercase tracking-wider">monitored</span>
                )}
              </div>
              <div className="text-[10px] text-surface-muted font-mono truncate">{acc.steamId64}</div>
            </div>
            <div className="text-center">{cs2Pill(cs2)}</div>
            <div className="text-surface-muted">
              {isVacBanned || isGameBanned
                ? <span className="text-steam-danger font-bold">{[
                    isVacBanned ? `VAC ${lbs.numberOfVACBans}` : null,
                    isGameBanned ? `GAME ${lbs.numberOfGameBans}` : null
                  ].filter(Boolean).join(' · ')}</span>
                : `${lbs.numberOfVACBans || 0} / ${lbs.numberOfGameBans || 0}`}
            </div>
            <div className="text-surface-text" title={cs2Played.absolute || 'Unknown — never seen, private profile, or pre-feature data'}>
              {cs2Played.relative}
            </div>
            <div className="text-surface-text" title={cs2HoursTooltip}>
              {cs2HoursLabel}
            </div>
            <div className="text-surface-text" title={online.absolute}>{online.relative}</div>
            <div className="text-surface-text" title={checked.absolute}>{checked.relative}</div>
            <div className="flex gap-1 justify-end">
              <button
                type="button"
                onClick={() => onOpenNotes(acc)}
                title={(acc.notesCount || 0) > 0 ? `View ${acc.notesCount} note${acc.notesCount === 1 ? '' : 's'}` : 'Add note'}
                className={`h-[26px] px-1 text-xs rounded-[2px] transition-colors flex items-center gap-1 ${
                  (acc.notesCount || 0) > 0
                    ? 'bg-surface-border/60 text-accent hover:bg-surface-border'
                    : 'bg-surface-border/60 text-surface-muted hover:bg-surface-border'
                }`}
              >
                <span>📝</span>
                {(acc.notesCount || 0) > 0 && <span className="text-[10px] font-bold">{acc.notesCount}</span>}
              </button>
              {role === 'manager' && cs2.kind === 'quarantined' && (
                <button
                  type="button"
                  onClick={() => onUnquarantine(acc._id)}
                  title="Clear quarantine"
                  className="px-2 h-[26px] bg-steam-warn text-surface-base text-[10px] font-bold uppercase rounded-[2px]"
                >Fix</button>
              )}
              {canCopy && isCredentialed && cs2.kind !== 'quarantined' && (
                <>
                  <IconButton icon="👤" onClick={() => onCopy('username', acc.username)} title="Copy username" />
                  <IconButton icon="🔑" onClick={() => onCopy('password', acc.password)} title="Copy password" />
                </>
              )}
              {role === 'manager' && isCredentialed && (
                <IconButton
                  icon={checkingIds?.has(acc._id) ? '⏳' : '🔄'}
                  onClick={() => onCheckNow(acc)}
                  title={checkingIds?.has(acc._id) ? 'Checking…' : 'Check cooldown now'}
                  disabled={checkingIds?.has(acc._id)}
                />
              )}
              {canCopy && !isCredentialed && cs2.kind !== 'quarantined' && (
                <>
                  <IconButton icon="👤" title="No username" color="muted" disabled />
                  <IconButton icon="🔑" title="No password" color="muted" disabled />
                </>
              )}
              {role === 'manager' && (
                <IconButton icon="✕" color="danger" onClick={() => onDelete(acc._id)} title="Delete" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
